import { datadogRum } from '@datadog/browser-rum';
import {
  createInstance,
  OptimizelyProvider,
  ReactSDKClient as OptimizelySDKClient,
  setLogger,
  setLogLevel,
} from '@optimizely/react-sdk';
import Cookies from 'js-cookie';

import {
  OPTIMIZELY_AB_TEST_REGISTRY,
  OptimizelyExperimentNameT,
  OptimizelyExperimentRegistryT,
  OptimizelyFeatureFlagT,
} from './experiments';
import {
  defaultOptimizelyMock,
  optimizelyMockOverrides,
  optimizelyVariationMockOverrides,
} from './optimizely-dev-state';
import {
  OptimizelyAbTestVariantResultT,
  OptimizelyAccessorT,
  OptimizelyDecisionSourceT,
  OptimizelyOptionsT,
} from './types';
import { APPLY_UUID_COOKIE_KEY } from '../constants';
import { isTruthy } from '../utils';

const {
  VITE_ENV,
  VITE_OPTIMIZELY_MOCKING,
  VITE_OPTIMIZELY_SDK_KEY,
} = import.meta.env;
const OPTIMIZELY_READY_TIMEOUT_MS = 3000;
const OPTLY_REFRESH_TIME_IN_MINUTES = 15;
const OPTLY_REFRESH_TIME = OPTLY_REFRESH_TIME_IN_MINUTES * 60 * 1000;

let optimizely: OptimizelySDKClient | undefined;

let optimizelyInitialized = false;

/**
 * Mocking check for development environments
 */
export const viteOptimizelyMockingEnabled = VITE_ENV === 'development' && VITE_OPTIMIZELY_MOCKING === 'true';

export const getApplyUuid = () => {
  const applyUuid = Cookies.get(APPLY_UUID_COOKIE_KEY) ?? '';

  if (!applyUuid
    && import.meta.env.VITE_MOCKING === 'true'
    && import.meta.env.VITE_ENV === 'development'
  ) {
    return 'mock-apply-uuid';
  }

  return applyUuid;
};

export const setApplyUuid = () => {
  const applyUuid = crypto.randomUUID();

  Cookies.set(APPLY_UUID_COOKIE_KEY, applyUuid, {
    domain: import.meta.env.VITE_NC_COOKIES_DOMAIN || '.netcredit.dev',
    expires: 183,
    path: '/',
    secure: import.meta.env.VITE_NC_COOKIES_SECURE === 'true',
  });

  return applyUuid;
};

/**
 * Builds the SDK client (and starts its datafile/event timers) on first call only, so merely
 * importing this module (tests, Storybook, other libs) doesn't spin up a live Optimizely client.
 */
const initOptimizely = (): void => {
  if (optimizelyInitialized) { return; }

  optimizelyInitialized = true;

  if (viteOptimizelyMockingEnabled) { return; }

  optimizely = createInstance({
    datafileOptions: {
      autoUpdate: true,
      updateInterval: OPTLY_REFRESH_TIME,
    },
    eventBatchSize: 100,
    eventFlushInterval: 1000,
    sdkKey: VITE_OPTIMIZELY_SDK_KEY,
  });

  // Don't log sensitive info in prod-like envs
  if (VITE_ENV === 'development') {
    setLogLevel('debug');
    setLogger(console);
  } else {
    setLogger(null);
  }

  optimizely.setUser({ id: Cookies.get(APPLY_UUID_COOKIE_KEY) || setApplyUuid() });
};

/**
 * Blocks app render until the Optimizely datafile has loaded (or the timeout elapses), so no
 * route — including the first one hit in the flow — can read a flag before a real decision exists.
*/
export const enableOptimizely = async () => {
  try {
    initOptimizely();

    if (!optimizely) { return; }

    const result = await optimizely.onReady({ timeout: OPTIMIZELY_READY_TIMEOUT_MS });

    if (!result?.success) {
      console.error('Optimizely did not become ready before app render');
      datadogRum.addError(new Error('Optimizely did not become ready before app render'), {
        context: 'enableOptimizely',
        reason: result?.reason,
      });
    }
  } catch (error) {
    // if there is an error during initialization, we just want to log the failure to load and get customers through
    datadogRum.addError(error, { context: 'Optimizely failed to load in #enableOptimizely' });
  }
};

export const NcBaseOptimizelyProvider = ({ accountId, children }: { accountId: string; children: React.ReactNode; }) => {
  if (!optimizely) {
    return children;
  }

  return (
    <OptimizelyProvider optimizely={optimizely} user={{ id: accountId }}>
      {children}
    </OptimizelyProvider>
  );
};

/**
 * Tracks optimizely decisions in Datadog using the flag-name and context
*/
export const trackOptimizelyDecision = (
  actionName: 'optimizelyAbTestVariant' | 'optimizelyFeatureDecision',
  featureKey: OptimizelyFeatureFlagT,
  context: {
    accessor: OptimizelyAccessorT;
    enabled?: boolean;
    source?: OptimizelyDecisionSourceT;
    variant?: string;
    variationKey?: string | null;
  },
): void => {
  datadogRum.addAction(actionName, { featureKey, ...context });
};

/**
 * Single source of truth for resolving a flag/experiment's enabled state and (unvalidated) variant
 * from either a live SDK client or the dev-mock override maps, gated by `viteOptimizelyMockingEnabled`.
 * Shared by the hooks (`useOptimizelyFeature`/`useOptimizelyAbTest`, passing their context-sourced
 * client) and the non-hook accessors below (passing the module-scope `optimizely` singleton) so
 * resolution logic — including the "no client/no mocking" error — can't drift between call paths.
 * Returns the shared `OptimizelyAbTestVariantResultT` shape; `variant` isn't validated against the
 * experiment registry until `resolveOptimizelyVariant`.
*/
export const resolveOptimizelyDecision = (
  client: OptimizelySDKClient | null | undefined,
  key: string,
  options?: OptimizelyOptionsT,
): OptimizelyAbTestVariantResultT => {
  if (isTruthy(client)) {
    const decision = client.decide(key, undefined, undefined, options);

    return {
      enabled: decision.enabled,
      variant: (decision.variationKey ?? undefined) as OptimizelyAbTestVariantResultT['variant'],
    };
  }

  if (viteOptimizelyMockingEnabled) {
    const variant = optimizelyVariationMockOverrides[key] ?? defaultOptimizelyMock.variationKey;

    return {
      enabled: key in optimizelyMockOverrides ? optimizelyMockOverrides[key] : defaultOptimizelyMock.enabled,
      variant: variant as OptimizelyAbTestVariantResultT['variant'],
    };
  }

  datadogRum.addError(
    `Error fetching Optimizely decision for "${key}"`,
    // eslint-disable-next-line @stylistic/object-curly-newline
    { key, options, viteOptimizelyMockingEnabled },
  );

  return { enabled: false, variant: undefined };
};

/**
 * Single source of truth for turning a resolved enabled/variant pair into a validated variant,
 * used by `getOptimizelyABTestVariant` below (the only caller — `useOptimizelyAbTest` also goes
 * through it, indirectly, by calling `getOptimizelyABTestVariant`). A variant that doesn't match any
 * registered variant is surfaced via the caller's `trackOptimizelyDecision` call, not tracked here,
 * so it isn't reported twice.
*/
export const resolveOptimizelyVariant = <ExperimentNameT extends OptimizelyExperimentNameT>(
  experimentName: ExperimentNameT,
  enabled: boolean, // if this is disabled, the method shouldn't be called at all
  variant: string | undefined,
): OptimizelyAbTestVariantResultT<ExperimentNameT> => {
  const variants = OPTIMIZELY_AB_TEST_REGISTRY[experimentName] as readonly string[];
  const validatedVariant = enabled && variant && variants.includes(variant)
    ? (variant as OptimizelyExperimentRegistryT[ExperimentNameT][number])
    : undefined;

  return {
    enabled,
    variant: validatedVariant,
  };
};

/**
 * Non-hook accessor so feature flags can be checked outside of React's render cycle
 * (eg. routing functions). Mirrors the mock/prod resolution used by `useOptimizelyFeature`, which
 * calls this directly (passing its context-sourced client) so the two call paths share one implementation.
*/
export const getOptimizelyFeatureEnabled = (
  featureKey: OptimizelyFeatureFlagT,
  options?: OptimizelyOptionsT,
  client: OptimizelySDKClient | null | undefined = optimizely,
): boolean => resolveOptimizelyDecision(client, featureKey, options).enabled;

/**
 * Resolves a user's A/B test variant. Used both outside of React's render cycle (eg. routing
 * functions, passing the module-scope client) and by `useOptimizelyAbTest` (passing its
 * context-sourced client and `skipTracking: true`) so variant-resolution can't drift between the
 * two call paths — the hook handles its own deduped tracking rather than tracking on every call.
*/
export const getOptimizelyABTestVariant = <ExperimentNameT extends OptimizelyExperimentNameT>(
  experimentName: ExperimentNameT,
  options?: OptimizelyOptionsT,
  client: OptimizelySDKClient | null | undefined = optimizely,
  skipTracking = false,
): OptimizelyAbTestVariantResultT<ExperimentNameT> => {
  const { enabled, variant: rawVariant } = resolveOptimizelyDecision(client, experimentName, options);

  if (!enabled) {
    return {
      enabled,
      variant: undefined,
    };
  }

  const { variant } = resolveOptimizelyVariant(experimentName, enabled, rawVariant);

  if (!skipTracking) {
    trackOptimizelyDecision('optimizelyAbTestVariant', experimentName, {
      accessor: 'nonHook',
      enabled,
      variant,
      variationKey: rawVariant,
    });
  }

  return {
    enabled,
    variant,
  };
};
